import { WarningIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionItem,
  AccordionIcon,
  AccordionPanel,
  AccordionButton,
  Box,
  Flex,
  Heading,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  SimpleGrid,
  Spacer,
  Stack,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  Text,
  Divider,
  IconButton,
  useColorModeValue,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { recurringDate } from '@finley-technologies/finley-date';
import { Date as dateType, Util } from '@finley-technologies/finley-types';

import { DateRangePicker } from '../DateRangePicker';
import { AddConfigButton } from './components/AddConfigButton';
import {
  DaysOfTheWeekSelectButtons,
  handleSelectDays,
} from './components/DayOfTheWeek';
import { ManualDatesComponent } from './components/ManualDates';
import { ReviewDatesComponentFromConfigFormState } from './components/ReviewDates';
import {
  DayOfTheWeek,
  dayOfTheWeekToNumberMap,
  ConfigFormState,
  PERIOD_OPTIONS,
  OFFSET_DAY_UNIT_OPTIONS,
} from './types';
import {
  isFullDateStrArray,
  isBaseRecurringDateConfig,
  convertConfigFormStatesToRecurringDateConfigs,
  generateHumanReadableString,
  makeAdverb,
  ordinalSuffix,
  convertRecurringDateConfigsToConfigFormStates,
} from './utils';
import { Icon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';

const RecurringDatesOptionsGrid: React.FC<{
  config: ConfigFormState;
  setConfig: React.Dispatch<React.SetStateAction<ConfigFormState[]>>;
  indexOfConfig: number;
  numOfConfigs: number;
}> = ({ config, setConfig, indexOfConfig }) => {
  const greyBgColor = useColorModeValue('gray.200', 'gray.800');

  // --------------------- REACT HOOKS ---------------------
  const [dateRange, setDateRange] = React.useState<{
    startDate: Date;
    endDate: Date;
  }>({
    startDate: config.startDate || new Date(),
    endDate: config.endDate || new Date(),
  });

  const [frequency, setFrequency] = React.useState<
    | dateType.RecurringDate.FrequencyPeriod
    | dateType.RecurringDate.ReccuringFrequencyPeriod
    | undefined
  >(config.frequency);

  const [cadence, setCadence] = React.useState(config.cadence || 1);
  const [occurrence, setOccurrence] = React.useState(config.occurrence || 1);

  const [
    period,
    setPeriod,
  ] = React.useState<dateType.RecurringDate.BaseDateConfigType>(
    config.period || 'calendarDate',
  );

  const frequencyFilteredOptions = useMemo(() => {
    const frequencyOptions = ['week', 'month', 'quarter', 'year'];

    if (period !== 'weekday') {
      return frequencyOptions.filter((f) => f !== 'week');
    }
    return ['week'];
  }, [period]);

  const [offsetDays, setOffsetDays] = React.useState<number>(
    config.offsetDays || 0,
  );
  const [offsetDayUnit, setoffsetDayUnit] = React.useState<dateType.DayUnit>(
    config.offsetDayUnit || 'calendarDay',
  );

  const [selectedDays, setSelectedDays] = React.useState<DayOfTheWeek[]>(
    config.selectedDays || [],
  );

  // --------------------- ERRORS FROM VALIDATING INPUTS ---------------------
  const [errorDateRange, setErrorDateRange] = React.useState<string>('');
  const [errorFrequency, setErrorFrequency] = React.useState<string>('');
  const [errorSelectedDay, setErrorSelectedDays] = React.useState<string>('');

  useEffect(() => {
    if (frequency) {
      setErrorFrequency(
        period !== 'weekday' && frequency === 'week'
          ? 'Frequency cannot be `week`'
          : '',
      );
    } else {
      setErrorFrequency('Frequency cannot be empty');
    }
    setErrorSelectedDays(
      (period === 'weekday' || period === 'weekdayByOccurrence') &&
        (!selectedDays || selectedDays.length < 1 || selectedDays.length > 7)
        ? 'Selected days of the week error'
        : '',
    );
    setErrorDateRange(
      !dateRange.startDate || !dateRange.endDate
        ? 'Missing start and/or end date'
        : '',
    );
  }, [
    dateRange.endDate,
    dateRange.startDate,
    frequency,
    occurrence,
    period,
    selectedDays,
  ]);

  const sumOfInputErrors = useMemo(() => {
    const err = [errorDateRange, errorFrequency, errorSelectedDay]
      .filter((e) => e.length > 0)
      .join(', ');
    return err;
  }, [errorDateRange, errorFrequency, errorSelectedDay]);

  // --------------------- VALIDATE & CREATE CONFIG ---------------------
  useEffect(() => {
    const result = recurringDate.validateAndCreateConfig(
      cadence,
      offsetDays,
      offsetDayUnit,
      period,
      selectedDays.map((day) => dayOfTheWeekToNumberMap[day]),
      occurrence,
      dateRange.startDate
        ? commonDate.dateToFullDateString(dateRange.startDate)
        : undefined,
      dateRange.endDate
        ? commonDate.dateToFullDateString(dateRange.endDate)
        : undefined,
      frequency,
    );
    setConfig((current) => {
      const newArr = [...current];
      newArr[indexOfConfig] = {
        isValid: !!result.config,
        isRelative: false,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        frequency,
        cadence,
        occurrence,
        period,
        offsetDays,
        offsetDayUnit,
        selectedDays,
      };
      return newArr;
    });
  }, [
    cadence,
    dateRange.endDate,
    dateRange.startDate,
    frequency,
    indexOfConfig,
    occurrence,
    offsetDayUnit,
    offsetDays,
    period,
    selectedDays,
    setConfig,
  ]);

  // --------------------- GENERATE HUMAN READABLE STRING ---------------------
  const getHumanReadableString = useCallback(() => {
    const startDateString = dateRange.startDate
      ? commonDate.dateToHumanDateString(dateRange.startDate)
      : undefined;
    const endDateString = dateRange.endDate
      ? commonDate.dateToHumanDateString(dateRange.endDate)
      : undefined;
    return generateHumanReadableString({
      cadence,
      offsetDayUnit,
      offsetDays,
      period,
      startDate: startDateString,
      startDateNumber: dateRange.startDate
        ? dateRange.startDate.getDate()
        : undefined,
      endDate: endDateString,
      frequency,
      selectedDays,
      occurrence,
    });
  }, [
    cadence,
    dateRange.endDate,
    dateRange.startDate,
    frequency,
    occurrence,
    offsetDayUnit,
    offsetDays,
    period,
    selectedDays,
  ]);

  return (
    <Stack spacing="20px">
      <HStack>
        <Box
          bg={greyBgColor}
          borderRadius="8px"
          padding="10px"
          fontSize="sm"
          w="95%"
        >
          {getHumanReadableString()}
        </Box>
        <Spacer />
        <IconButton
          aria-label="Delete config"
          icon={<Icon icon="trash-can" />}
          size="sm"
          variant="ghost"
          onClick={() => {
            setConfig((current) => {
              const newArr = [...current];
              newArr.splice(indexOfConfig, 1);
              return newArr;
            });
          }}
        />
      </HStack>

      <SimpleGrid
        columns={2}
        spacingX="40px"
        spacingY="20px"
        templateColumns="1fr 4fr"
      >
        <Text fontSize="md" mt={2}>
          Date Range
        </Text>
        <DateRangePicker
          onSelectDateRange={(newDateRange) => {
            const [newStartDate, newEndDate] = newDateRange;
            if (newStartDate && newEndDate) {
              setDateRange({
                startDate: newStartDate,
                endDate: newEndDate,
              });
            }
          }}
          currentDateRange={{
            chartDataStartDate: dateRange.startDate,
            chartDataEndDate: dateRange.endDate,
          }}
          width="100%"
        />

        <Text fontSize="md" mt={2}>
          Period
        </Text>
        <Select
          value={period}
          options={PERIOD_OPTIONS}
          onChange={(value) => {
            setPeriod(value as dateType.RecurringDate.BaseDateConfigType);
            if (value === 'weekday') {
              setFrequency('week');
            }
            if (value !== 'weekday' && frequency === 'week') {
              setFrequency('month');
            }
            setSelectedDays([]);
          }}
        />

        <Text fontSize="md" mt={2}>
          Frequency
        </Text>
        <Select
          placeholderLabel="Select frequency"
          value={frequency}
          isInvalid={errorFrequency.length > 0}
          options={_.map(frequencyFilteredOptions, (option) => ({
            label: makeAdverb(option),
            value: option,
          }))}
          onChange={(value) => {
            setFrequency(
              value as dateType.RecurringDate.FrequencyPeriod | undefined,
            );
          }}
        />

        <Text fontSize="md" mt={2}>
          Repeat every
        </Text>
        <HStack spacing="20px" width="76%" borderRadius="8px" height="40px">
          <NumberInput
            value={cadence}
            min={1}
            onChange={(newValue) => setCadence(Number(newValue))}
            width="82px"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>

          <Text fontSize="md">frequency</Text>
        </HStack>

        {(period === 'weekday' || period === 'weekdayByOccurrence') && (
          <Text fontSize="md" mt={2} />
        )}
        {(period === 'weekday' || period === 'weekdayByOccurrence') && (
          <DaysOfTheWeekSelectButtons
            onSelectDay={(day) => handleSelectDays(day, setSelectedDays)}
            selectedDays={selectedDays}
            error={errorSelectedDay}
          />
        )}

        {period === 'weekdayByOccurrence' && <Text fontSize="md" mt={2} />}
        {period === 'weekdayByOccurrence' && (
          <NumberInput
            value={ordinalSuffix(occurrence)}
            min={1}
            onChange={(newValue) => setOccurrence(Number(newValue))}
            width="82px"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        )}

        <Text fontSize="md" mt={2}>
          Offset
        </Text>
        <HStack spacing="20px" width="100%" borderRadius="8px" height="40px">
          <NumberInput
            value={offsetDays}
            onChange={(newValue) => setOffsetDays(Number(newValue))}
            width="105px"
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Select
            value={offsetDayUnit}
            options={OFFSET_DAY_UNIT_OPTIONS}
            onChange={(value) => {
              setoffsetDayUnit(value as dateType.DayUnit);
            }}
          />
        </HStack>
      </SimpleGrid>

      {(!config.isValid || sumOfInputErrors.length > 0) && (
        <HStack
          spacing="8px"
          bg="red.100"
          borderRadius="8px"
          padding="15px"
          fontSize="sm"
        >
          <WarningIcon color="red.500" />
          <Text color="red.500">{sumOfInputErrors}</Text>
        </HStack>
      )}

      <Divider orientation="horizontal" borderColor="gray.300" />

      <Spacer />
    </Stack>
  );
};

export const RecurringDateForm: React.FC<{
  savedConfig: dateType.RecurringDate.Config;
  setSavedConfig: React.Dispatch<
    React.SetStateAction<dateType.RecurringDate.Config>
  >;
}> = ({ savedConfig, setSavedConfig }) => {
  // configs
  const savedRecurringDateConfigs = savedConfig.recurringDateConfigs.filter(
    isBaseRecurringDateConfig,
  );

  const [recurringDateConfigs, setRecurringDateConfigs] = React.useState<
    ConfigFormState[]
  >(
    savedRecurringDateConfigs.length > 0
      ? convertRecurringDateConfigsToConfigFormStates(savedRecurringDateConfigs)
      : [{ isValid: false, isRelative: false }],
  );

  const savedExcludedDateConfigs = savedConfig.exclusions?.filter(
    isBaseRecurringDateConfig,
  );

  const [excludedDateConfigs, setExcludedDateConfigs] = React.useState<
    ConfigFormState[]
  >(
    savedExcludedDateConfigs && savedExcludedDateConfigs.length > 0
      ? convertRecurringDateConfigsToConfigFormStates(savedExcludedDateConfigs)
      : [{ isValid: false, isRelative: false }],
  );

  // datelists
  const [manualAddDates, setManualAddDates] = React.useState<
    Util.FullDateStr[]
  >(savedConfig.recurringDateConfigs.filter(isFullDateStrArray).flat() || []);
  const [manualExcludeDates, setManualExcludeDates] = React.useState<
    Util.FullDateStr[]
  >(savedConfig.exclusions?.filter(isFullDateStrArray).flat() || []);

  // number of valid dates in review date accordion
  const [reviewDatesCount, setReviewDatesCount] = React.useState<number>(0);

  // update state that is passed in
  useEffect(() => {
    const allRecurring = convertConfigFormStatesToRecurringDateConfigs(
      recurringDateConfigs.filter((c) => c.isValid && !c.isRelative),
    ).concat(manualAddDates.length > 0 ? [manualAddDates] : []);
    const allExcluded = convertConfigFormStatesToRecurringDateConfigs(
      excludedDateConfigs.filter((c) => c.isValid && !c.isRelative),
    ).concat(manualExcludeDates.length > 0 ? [manualExcludeDates] : []);
    const isDisabled = !recurringDateConfigs.every((c) => c.isValid);
    if (!isDisabled) {
      setSavedConfig({
        recurringDateConfigs: allRecurring,
        exclusions: allExcluded,
      });
    }
  }, [
    excludedDateConfigs,
    manualAddDates,
    manualExcludeDates,
    recurringDateConfigs,
    setSavedConfig,
  ]);

  return (
    <Tabs>
      <TabList align="center">
        <Tab>Recurring Dates</Tab>
        <Tab>Optional Exclusions</Tab>
        <Tab>Manual Dates</Tab>
      </TabList>

      <TabPanels>
        <TabPanel>
          <Accordion defaultIndex={[0]} allowMultiple allowToggle width="650px">
            <Stack spacing="25px">
              <AccordionItem
                border="1px solid"
                borderColor="gray.300"
                borderRadius="lg"
                padding="20px"
              >
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    <Heading as="h4" size="md">
                      Recurring Dates
                    </Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel mt="16px" pb={4}>
                  <Flex direction="column" spacing="20px">
                    {_.map(recurringDateConfigs, (config, i) => {
                      return (
                        <RecurringDatesOptionsGrid
                          key={JSON.stringify(config)}
                          config={config}
                          setConfig={setRecurringDateConfigs}
                          indexOfConfig={i}
                          numOfConfigs={recurringDateConfigs.length}
                        />
                      );
                    })}
                    <AddConfigButton
                      title="Add Recurring Dates"
                      configs={recurringDateConfigs}
                      setConfig={setRecurringDateConfigs}
                    />
                  </Flex>
                </AccordionPanel>
              </AccordionItem>

              <AccordionItem
                border="1px solid"
                borderColor="gray.300"
                borderRadius="lg"
                padding="20px"
              >
                <AccordionButton>
                  <Box as="span" flex="1" textAlign="left">
                    <Heading as="h4" size="md">
                      {`Review Dates (${reviewDatesCount})`}
                    </Heading>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel mt="16px" pb={4}>
                  <ReviewDatesComponentFromConfigFormState
                    recurringConfigs={recurringDateConfigs}
                    excludedConfigs={excludedDateConfigs}
                    manualAddDates={manualAddDates}
                    manualExcludeDates={manualExcludeDates}
                    setReviewDatesCount={(num: number) =>
                      setReviewDatesCount(num)
                    }
                  />
                </AccordionPanel>
              </AccordionItem>
            </Stack>
          </Accordion>
        </TabPanel>

        <TabPanel>
          <Stack
            border="1px solid"
            borderColor="gray.300"
            borderRadius="lg"
            padding="20px"
            spacing="25px"
            width="650px"
          >
            <Heading as="h4" size="md">
              Optional Exclusions
            </Heading>

            <Text fontSize="md">The dates listed here will be excluded.</Text>
            <Flex direction="column" spacing="20px">
              {_.map(excludedDateConfigs, (config, i) => {
                return (
                  <RecurringDatesOptionsGrid
                    key={JSON.stringify(config)}
                    config={config}
                    setConfig={setExcludedDateConfigs}
                    indexOfConfig={i}
                    numOfConfigs={excludedDateConfigs.length}
                  />
                );
              })}
              <AddConfigButton
                title="Add Exclusion Dates"
                configs={excludedDateConfigs}
                setConfig={setExcludedDateConfigs}
              />
            </Flex>
          </Stack>
        </TabPanel>

        <TabPanel>
          <Flex direction="column">
            <Stack spacing="20px" width="650px">
              <ManualDatesComponent
                title="Add"
                dateList={manualAddDates}
                setManualDates={setManualAddDates}
              />

              <ManualDatesComponent
                title="Exclude"
                dateList={manualExcludeDates}
                setManualDates={setManualExcludeDates}
              />
            </Stack>
          </Flex>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
